import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import { getTokenSilently, doLogout } from "@/utils/auth";
import { CURRENT_WORKSPACE_ID, isBrowser } from "@/utils/local_storage_keys";

interface CreateAuthenticatedClientOptions {
  baseURL?: string;
  defaultHeaders?: Record<string, string>;
  includeWorkspaceId?: boolean;
}

const getCurrentWorkspaceId = (): string | null => {
  if (!isBrowser) return null;
  return localStorage.getItem(CURRENT_WORKSPACE_ID);
};

/**
 * Creates an authenticated axios instance that automatically:
 * 1. Gets a valid token from the Auth0 SDK before each request
 * 2. Retries failed requests after token refresh on 401
 * 3. Adds workspace ID header if needed
 */
export const createAuthenticatedClient = (
  options: CreateAuthenticatedClientOptions = {}
): AxiosInstance => {
  const {
    baseURL = import.meta.env.VITE_COFFEE_BASE_API_URL,
    defaultHeaders = {},
    includeWorkspaceId = true,
  } = options;

  const instance = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      "Content-Type": "application/json",
      ...defaultHeaders,
    },
  });

  // Request interceptor — get token from SDK (handles refresh automatically)
  instance.interceptors.request.use(
    async (config) => {
      try {
        const token = await getTokenSilently();

        if (token) {
          config.headers = config.headers || {} as any;
          (config.headers as any).Authorization = `Bearer ${token}`;
        } else {
          console.warn('No valid auth token available for request');
        }

        if (includeWorkspaceId) {
          const workspaceId = getCurrentWorkspaceId();
          if (workspaceId) {
            config.headers = config.headers || {} as any;
            (config.headers as any)["x-workspace-id"] = workspaceId;
          }
        }
      } catch (error) {
        console.error('Error getting valid token for request:', error);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor — force-refresh token on 401, retry once
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const newToken = await getTokenSilently({ cacheMode: 'off' } as any);

          if (newToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance.request(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }

        // If refresh failed, let the error propagate — don't force logout
        // The AuthProvider will handle redirecting to login
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * Default authenticated client for Coffee API
 */
export const authenticatedHttp = createAuthenticatedClient();

/**
 * Base URL of the Visitor ID service.
 *
 * Single source of truth on purpose: this host is not only where the app's
 * API calls go, it is also baked into the <script> snippets we tell
 * customers to paste into their site (pixel + forms tag). Those two used
 * to be declared separately and had drifted — the client defaulted to prd
 * while the snippets defaulted to a *dev* host, so a customer copying the
 * snippet from an env without VITE_VISITOR_ID_API_URL set would install a
 * tag pointing at dev. Derive both from here so they cannot diverge again.
 */
export const VISITOR_ID_BASE_URL =
  import.meta.env.VITE_VISITOR_ID_API_URL || 'https://visitor-id.prd.coffee.work';

/**
 * Authenticated client for the Visitor ID API
 */
export const visitorIdHttp = createAuthenticatedClient({
  baseURL: VISITOR_ID_BASE_URL,
});

/**
 * Helper function for making authenticated fetch requests
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  try {
    const token = await getTokenSilently();

    const headers: Record<string, string> = {};

    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const workspaceId = getCurrentWorkspaceId();
    if (workspaceId) {
      headers['x-workspace-id'] = workspaceId;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 by force-refreshing token and retrying once
    if (response.status === 401) {
      try {
        const newToken = await getTokenSilently({ cacheMode: 'off' } as any);
        if (newToken) {
          headers['Authorization'] = `Bearer ${newToken}`;
          return fetch(url, {
            ...options,
            headers,
          });
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }
    }

    return response;
  } catch (error) {
    console.error('Authenticated fetch error:', error);
    throw error;
  }
};
